import type { ScaleLinear, ScaleTime } from 'd3';
import type { Data } from '@/types/chart';
import type { LineChartValues } from '../types/LineChartValues';

export const getClosestLine = (
  data: LineChartValues[],
  accessor: { getValue: (d: Data) => number; getDate: (d: Data) => Date },
  xScale: ScaleTime<number, number, never>,
  yScale: ScaleLinear<number, number, never>,
  margin: number,
  mousePos: { x: number; y: number },
) => {
  const { x: mx, y: my } = mousePos;
  let newBestDist = Infinity;
  let best: {
    dist: number;
    coords: { x: number; y: number };
    data: Data & { name: string; id: number | string; groupIndex: number };
  } | null = null;

  // lower-bound on pixel x without building an array
  const lbx = (arr: Data[], targetX: number) => {
    let lo = 1,
      hi = arr.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      const xmid = margin + xScale(accessor.getDate(arr[mid]));
      if (xmid < targetX) lo = mid + 1;
      else hi = mid - 1;
    }
    return lo;
  };

  for (let s = 0; s < data.length; s++) {
    const row = data[s];
    if (!row?.visible) continue;
    const arr = row.data;
    const n = arr?.length ?? 0;
    if (!n) continue;

    const x0 = margin + xScale(accessor.getDate(arr[0]));
    const xN = margin + xScale(accessor.getDate(arr[n - 1]));

    // If only one point, or mouse left/right of the range, compare endpoint(s) only
    if (n === 1 || mx <= x0 || mx >= xN) {
      const i = n === 1 ? 0 : mx <= x0 ? 0 : n - 1;
      const d = arr[i];
      const px = margin + xScale(accessor.getDate(d));
      const py = yScale(accessor.getValue(d));
      const dx = px - mx,
        dy = py - my,
        newDist = dx * dx + dy * dy;
      if (newDist < newBestDist) {
        newBestDist = newDist;
        best = {
          dist: Math.sqrt(newDist),
          coords: { x: px, y: py },
          data: { ...d, name: row.name, id: row.id, groupIndex: row.groupIndex },
        };
      }
      continue;
    }

    // Find segment around mouse x in pixel space
    let i = lbx(arr, mx);
    if (i <= 0) i = 1;
    else if (i >= n) i = n - 1;

    // y ↑
    // |
    // |   (x0p, y0p)   ●────────────●   (x1p, y1p)
    // |
    // +--------------------------------------→ x
    //                margin

    const d0 = arr[i - 1],
      d1 = arr[i];
    const x0p = margin + xScale(accessor.getDate(d0));
    const y0p = yScale(accessor.getValue(d0));
    const x1p = margin + xScale(accessor.getDate(d1));
    const y1p = yScale(accessor.getValue(d1));

    // Snap to whichever endpoint is closest in pixel space
    const dx0 = x0p - mx,
      dy0 = y0p - my;
    const dx1 = x1p - mx,
      dy1 = y1p - my;
    const dist0 = dx0 * dx0 + dy0 * dy0;
    const dist1 = dx1 * dx1 + dy1 * dy1;
    const useD0 = dist0 <= dist1;
    const dist2 = useD0 ? dist0 : dist1;

    if (dist2 < newBestDist) {
      newBestDist = dist2;
      const pick = useD0 ? d0 : d1;
      const px = useD0 ? x0p : x1p;
      const py = useD0 ? y0p : y1p;
      best = {
        dist: Math.sqrt(dist2),
        coords: { x: px, y: py },
        data: { ...pick, name: row.name, id: row.id, groupIndex: row.groupIndex },
      };
    }
  }

  return best;
};
